import { useEffect, useRef, useState } from 'react';
import { prefersReducedMotion } from '../hooks/useReducedMotion';

export interface Step {
  title: string;
  detail: string;
}

interface FlowProps {
  steps: readonly Step[];
  active: boolean;
  runKey: number;
  onStep?: (index: number) => void;
  onDone?: () => void;
}

const STEP_MS = 620;

export function Flow({ steps, active, runKey, onStep, onDone }: FlowProps) {
  const [stage, setStage] = useState(-1);
  const timers = useRef<number[]>([]);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  const stepRef = useRef(onStep);
  stepRef.current = onStep;

  useEffect(() => {
    for (const t of timers.current) window.clearTimeout(t);
    timers.current = [];

    if (!active) {
      setStage(-1);
      return;
    }

    if (prefersReducedMotion()) {
      setStage(steps.length);
      for (let i = 0; i < steps.length; i += 1) stepRef.current?.(i);
      doneRef.current?.();
      return;
    }

    setStage(-1);
    for (let i = 0; i < steps.length; i += 1) {
      timers.current.push(
        window.setTimeout(() => {
          setStage(i);
          stepRef.current?.(i);
        }, 180 + i * STEP_MS),
      );
    }
    timers.current.push(
      window.setTimeout(() => {
        setStage(steps.length);
        doneRef.current?.();
      }, 180 + steps.length * STEP_MS),
    );

    return () => {
      for (const t of timers.current) window.clearTimeout(t);
      timers.current = [];
    };
  }, [steps, active, runKey]);

  return (
    <ol className="mk-flow">
      {steps.map((step, i) => {
        const state = stage > i ? 'done' : stage === i ? 'active' : 'idle';
        return (
          <li className="mk-step" data-state={state} key={step.title}>
            <span className="mk-step-rail" aria-hidden="true">
              <span className="mk-step-dot">{i + 1}</span>
            </span>
            <div className="mk-step-body">
              <p className="mk-step-title">{step.title}</p>
              <p className="mk-step-detail">{step.detail}</p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
